// src/components/attendance/SubmitAttendance.jsx
// Reusable punch-in / punch-out widget. Drop into any dashboard:
//   import SubmitAttendance from '../attendance/SubmitAttendance';
//   <SubmitAttendance />

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { attendanceAPI } from '../../services/api';

// ─── Inline styles (CSS-variable-based, works in any portal) ─────────────────
const CSS = `
  @keyframes sa-spin  { to { transform: rotate(360deg); } }
  @keyframes sa-pulse { 0%,100%{opacity:1} 50%{opacity:.5} }
  @keyframes sa-pop   { 0%{transform:scale(.92);opacity:0} 100%{transform:scale(1);opacity:1} }
  @keyframes sa-progress { from { width: 0% } to { width: 100% } }
  .sa-root { animation: sa-pop .25s ease both; }
  .sa-btn:hover:not(:disabled) { filter: brightness(.92); }
  .sa-btn:active:not(:disabled) { transform: scale(.96); }
  .sa-btn { transition: filter .15s, transform .1s; }
  .sa-shift-bar { transition: background .2s; }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtTime = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
};

const fmtDate = () =>
  new Date().toLocaleDateString('en-IN', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

const padNum = n => String(Math.floor(n)).padStart(2, '0');

const fmtHours = h => {
  const num   = parseFloat(h) || 0;
  const hrs   = Math.floor(num);
  const mins  = Math.round((num - hrs) * 60);
  return `${padNum(hrs)}h ${padNum(mins)}m`;
};

/**
 * Convert "HH:MM" (24-h) to a 12-h display string.
 * e.g. "09:00" → "9:00 AM", "17:30" → "5:30 PM"
 */
const fmt24to12 = time24 => {
  if (!time24) return '—';
  const [hStr, mStr] = time24.split(':');
  const h = parseInt(hStr, 10);
  const m = mStr || '00';
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12  = h % 12 || 12;
  return `${h12}:${m} ${ampm}`;
};

/**
 * Given shift start/end strings ("HH:MM"), compute how far through
 * the shift we currently are as a 0–1 fraction.
 */
const shiftProgress = (startStr, endStr) => {
  if (!startStr || !endStr) return null;
  const now   = new Date();
  const today = now.toISOString().slice(0, 10);
  const start = new Date(`${today}T${startStr}:00`);
  let   end   = new Date(`${today}T${endStr}:00`);
  if (end <= start) end = new Date(end.getTime() + 86400000); // overnight

  const total   = end - start;
  const elapsed = now - start;
  if (elapsed < 0)      return 0;
  if (elapsed > total)  return 1;
  return elapsed / total;
};

const STATUS_META = {
  PRESENT: { label: 'Present',  color: '#3d9e6e', bg: '#ecf7f2', dot: '#3d9e6e' },
  ABSENT:  { label: 'Absent',   color: '#c95f5f', bg: '#fdf0f0', dot: '#c95f5f' },
  LEAVE:   { label: 'On Leave', color: '#b5882a', bg: '#fdf6e7', dot: '#b5882a' },
  HOLIDAY: { label: 'Holiday',  color: '#4a7fc1', bg: '#eef3fb', dot: '#4a7fc1' },
};

// ─── Live clock ───────────────────────────────────────────────────────────────
function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

// ─── Live work-hours tracker ─────────────────────────────────────────────────
function useLiveHours(punchInIso, punchOutIso, serverHours) {
  const [liveH, setLiveH] = useState(parseFloat(serverHours) || 0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!punchInIso || punchOutIso) {
      setLiveH(parseFloat(serverHours) || 0);
      return;
    }
    const pinTime = new Date(punchInIso).getTime();
    const tick = () => {
      const secs = (Date.now() - pinTime) / 1000;
      setLiveH(Math.max(secs / 3600, 0));
      rafRef.current = setTimeout(tick, 10000); // update every 10 s
    };
    tick();
    return () => clearTimeout(rafRef.current);
  }, [punchInIso, punchOutIso, serverHours]);

  return liveH;
}

// ─── Shift Progress Bar ───────────────────────────────────────────────────────
function ShiftProgressBar({ shift, punchInIso, punchOutIso }) {
  const [progress, setProgress] = useState(
    () => shiftProgress(shift?.start_time, shift?.end_time) || 0
  );

  useEffect(() => {
    if (!shift) return;
    const update = () =>
      setProgress(shiftProgress(shift.start_time, shift.end_time) || 0);
    update();
    const id = setInterval(update, 30000); // refresh every 30 s
    return () => clearInterval(id);
  }, [shift]);

  if (!shift) return null;

  const pct        = Math.round(progress * 100);
  const isActive   = punchInIso && !punchOutIso;
  const isComplete = pct >= 100;
  const barColor   = isComplete ? '#3d9e6e' : isActive ? '#4a7fc1' : '#9aa3b4';

  return (
    <div style={{ marginBottom: 18 }}>
      {/* Shift timing row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Shift badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '4px 10px',
            borderRadius: 20,
            background: '#f0f4ff',
            border: '1px solid #c5d8f0',
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#4a7fc1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#4a7fc1', letterSpacing: '.02em' }}>
              {shift.name}
            </span>
          </div>
        </div>

        {/* Start → End times */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 13,
          fontWeight: 600,
          color: '#3a4459',
          fontVariantNumeric: 'tabular-nums',
        }}>
          <span style={{
            padding: '3px 8px',
            background: '#ecf7f2',
            borderRadius: 6,
            border: '1px solid #b2dcc8',
            color: '#3d9e6e',
            fontSize: 12,
            fontWeight: 700,
          }}>
            {fmt24to12(shift.start_time)}
          </span>
          <span style={{ color: '#9aa3b4', fontSize: 11 }}>→</span>
          <span style={{
            padding: '3px 8px',
            background: '#fdf0f0',
            borderRadius: 6,
            border: '1px solid #e8baba',
            color: '#c95f5f',
            fontSize: 12,
            fontWeight: 700,
          }}>
            {fmt24to12(shift.end_time)}
          </span>
          {/* Duration pill */}
          {shift.duration_display && (
            <span style={{
              padding: '3px 8px',
              background: '#f4f6f9',
              borderRadius: 6,
              border: '1px solid #e2e6ed',
              color: '#5a6478',
              fontSize: 11,
              fontWeight: 600,
            }}>
              {shift.duration_display}
            </span>
          )}
        </div>
      </div>

      {/* Progress track */}
      <div style={{
        height: 6,
        borderRadius: 4,
        background: '#eef0f4',
        overflow: 'hidden',
        position: 'relative',
      }}>
        <div
          className="sa-shift-bar"
          style={{
            height: '100%',
            width: `${pct}%`,
            borderRadius: 4,
            background: barColor,
            transition: 'width 1s linear',
            position: 'relative',
          }}
        >
          {/* Animated shimmer when active */}
          {isActive && (
            <div style={{
              position: 'absolute',
              top: 0, right: 0,
              width: 40, height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,.5), transparent)',
              animation: 'sa-progress 2s ease-in-out infinite',
            }} />
          )}
        </div>
      </div>

      {/* Percentage label */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: 4,
        fontSize: 10,
        color: '#9aa3b4',
        fontWeight: 600,
      }}>
        <span>Shift start</span>
        <span style={{ color: barColor }}>
          {isComplete
            ? '✓ Shift complete'
            : `${pct}% elapsed`}
        </span>
        <span>Shift end</span>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function SubmitAttendance({ compact = false }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState('');
  const [message, setMessage] = useState('');
  const clock = useClock();

  const liveHours = useLiveHours(
    data?.punch_in_ist,
    data?.punch_out_ist,
    data?.total_work_hours,
  );

  // ── Fetch today's status ───────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await attendanceAPI.getToday();
      setData(res);
    } catch (e) {
      setError('Could not load attendance. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();                                   // immediate fetch

    const intervalId = setInterval(() => {
      // Only poll when the document is visible (saves requests when tab is hidden)
      if (!document.hidden) {
        refresh();
      }
    }, 30_000);                                  // 30 seconds

    // Also refresh immediately when the user switches back to this tab
    const handleVisibility = () => {
      if (!document.hidden) refresh();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [refresh]);

  // ── Punch action ──────────────────────────────────────────────────────────
  const handlePunch = async punchType => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const res = await attendanceAPI.punch();
      setData(res.today_status);
      setMessage(`${punchType === 'IN' ? 'Punched In' : 'Punched Out'} at ${fmtTime(new Date().toISOString())}`);
    } catch (e) {
      setError(e.response?.data?.error || 'Punch failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const meta    = STATUS_META[data?.status] || STATUS_META.ABSENT;
  const timeStr = clock.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });

  if (loading) {
    return (
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e6ed', padding: 24, boxShadow: '0 2px 12px rgba(0,0,0,.06)' }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
          <div style={{ width: 24, height: 24, border: '2px solid #e2e6ed', borderTop: '2px solid #4a7fc1', borderRadius: '50%', animation: 'sa-spin .7s linear infinite' }} />
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{CSS}</style>
      <div className="sa-root" style={{
        background: '#fff',
        borderRadius: 14,
        border: '1px solid #e2e6ed',
        boxShadow: '0 2px 12px rgba(0,0,0,.07)',
        overflow: 'hidden',
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
      }}>

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div style={{
          padding: compact ? '14px 18px' : '18px 22px',
          background: 'linear-gradient(135deg, #f8f9fb 0%, #eef3fb 100%)',
          borderBottom: '1px solid #e2e6ed',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 8,
        }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: '#9aa3b4', marginBottom: 2 }}>
              Attendance
            </div>
            <div style={{ fontSize: compact ? 13 : 15, fontWeight: 700, color: '#1e2636', fontFamily: "'Lora', serif" }}>
              {fmtDate()}
            </div>
          </div>
        </div>

        {/* ── Body ──────────────────────────────────────────────────────── */}
        <div style={{ padding: compact ? '14px 18px' : '20px 22px' }}>

          {/* Status badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 14px',
              borderRadius: 20,
              background: meta.bg,
              border: `1px solid ${meta.color}33`,
              fontSize: 13,
              fontWeight: 700,
              color: meta.color,
            }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%', background: meta.dot,
                ...(data?.status === 'PRESENT' && !data?.punch_out_ist ? {
                  animation: 'sa-pulse 1.5s ease-in-out infinite',
                } : {}),
              }} />
              {meta.label}
              {data?.is_late && (
                <span style={{
                  fontSize: 10,
                  marginLeft: 4,
                  padding: '1px 6px',
                  background: '#fdf6e7',
                  border: '1px solid #e8d4a0',
                  borderRadius: 10,
                  color: '#b5882a',
                }}>
                  Late
                </span>
              )}
            </span>
          </div>

          {/* ── Shift timing block ──────────────────────────────────────── */}
          {data?.shift ? (
            <ShiftProgressBar
              shift={data.shift}
              punchInIso={data.punch_in_ist}
              punchOutIso={data.punch_out_ist}
            />
          ) : (
            <div style={{
              marginBottom: 16,
              padding: '10px 14px',
              borderRadius: 8,
              background: '#fdf6e7',
              border: '1px solid #e8d4a0',
              fontSize: 12,
              color: '#b5882a',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              No shift assigned — contact your admin.
            </div>
          )}

          {/* Punch details grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: compact ? '1fr 1fr' : '1fr 1fr 1fr',
            gap: 10,
            marginBottom: 18,
          }}>
            <PunchCard
              label="Punch In"
              value={fmtTime(data?.punch_in_ist)}
              subValue={data?.shift ? `Expected by ${fmt24to12(data.shift.start_time)}` : null}
              icon="↓"
              color="#3d9e6e"
              active={!!data?.punch_in_ist}
            />
            <PunchCard
              label="Punch Out"
              value={fmtTime(data?.punch_out_ist)}
              subValue={data?.shift ? `Shift ends ${fmt24to12(data.shift.end_time)}` : null}
              icon="↑"
              color="#c95f5f"
              active={!!data?.punch_out_ist}
            />
            {!compact && (
              <PunchCard
                label="Work Hours"
                value={fmtHours(liveHours)}
                subValue={data?.shift?.duration_display ? `of ${data.shift.duration_display}` : null}
                icon="⏱"
                color="#4a7fc1"
                active={liveHours > 0}
                live={!!data?.punch_in_ist && !data?.punch_out_ist}
              />
            )}
          </div>

          {/* Error / success messages */}
          {error && (
            <div style={{ padding: '8px 12px', borderRadius: 8, background: '#fdf0f0', border: '1px solid #e8baba', color: '#c95f5f', fontSize: 13, marginBottom: 14 }}>
              {error}
            </div>
          )}
          {message && !error && (
            <div style={{ padding: '8px 12px', borderRadius: 8, background: '#ecf7f2', border: '1px solid #b2dcc8', color: '#3d9e6e', fontSize: 13, marginBottom: 14 }}>
              ✓ {message}
            </div>
          )}

          {/* Punch buttons */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              className="sa-btn"
              disabled={!data?.can_punch_in || busy}
              onClick={() => handlePunch('IN')}
              style={{
                flex: 1,
                padding: compact ? '10px 14px' : '12px 18px',
                borderRadius: 10,
                border: 'none',
                cursor: (!data?.can_punch_in || busy) ? 'not-allowed' : 'pointer',
                background: data?.can_punch_in ? '#3d9e6e' : '#e8e9eb',
                color: data?.can_punch_in ? '#fff' : '#9aa3b4',
                fontSize: 14,
                fontWeight: 700,
                fontFamily: 'inherit',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                opacity: (!data?.can_punch_in || busy) ? .65 : 1,
              }}
            >
              {busy && data?.can_punch_in ? (
                <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,.4)', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'sa-spin .7s linear infinite' }} />
              ) : '↓'}
              Punch In
            </button>

            <button
              className="sa-btn"
              disabled={!data?.can_punch_out || busy}
              onClick={() => handlePunch('OUT')}
              style={{
                flex: 1,
                padding: compact ? '10px 14px' : '12px 18px',
                borderRadius: 10,
                border: 'none',
                cursor: (!data?.can_punch_out || busy) ? 'not-allowed' : 'pointer',
                background: data?.can_punch_out ? '#c95f5f' : '#e8e9eb',
                color: data?.can_punch_out ? '#fff' : '#9aa3b4',
                fontSize: 14,
                fontWeight: 700,
                fontFamily: 'inherit',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                opacity: (!data?.can_punch_out || busy) ? .65 : 1,
              }}
            >
              {busy && data?.can_punch_out ? (
                <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,.4)', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'sa-spin .7s linear infinite' }} />
              ) : '↑'}
              Punch Out
            </button>
          </div>

          {/* Joining date note */}
          {data?.joining_date && (
            <div style={{ marginTop: 12, fontSize: 11, color: '#9aa3b4', textAlign: 'center' }}>
              Attendance tracked from {new Date(data.joining_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Sub-component: punch info card ──────────────────────────────────────────
function PunchCard({ label, value, subValue, icon, color, active, live }) {
  return (
    <div style={{
      background: active ? color + '0d' : '#f8f9fb',
      borderRadius: 10,
      border: `1px solid ${active ? color + '33' : '#e2e6ed'}`,
      padding: '10px 12px',
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: active ? color : '#9aa3b4', marginBottom: 4 }}>
        {icon} {label}
        {live && <span style={{ marginLeft: 5, animation: 'sa-pulse 1.2s ease-in-out infinite' }}>●</span>}
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: active ? color : '#c0c4cd', fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
      {subValue && (
        <div style={{ fontSize: 10, color: '#9aa3b4', marginTop: 3, fontWeight: 500 }}>
          {subValue}
        </div>
      )}
    </div>
  );
}